import React, { useCallback, useEffect, useMemo } from 'react'
import cn from 'clsx'
import { rem } from 'polished'
import styled from 'styled-components'
import config from 'constants/config'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import { OptimizelyExperiments } from 'constants/optimizely'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { getOptimizelyExperimentVariation } from 'lib/optimizely/optimizelyUtils'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getPackageViewPricingPerNight } from 'lib/offer/getPackageViewPricingPerNightUtils'
import { OfferPackageOptionView, getCheapestSelectedPackageOptionWithDefault } from 'selectors/offerPage/offerPageSelectors'
import Group from 'components/utils/Group'
import Clickable from 'components/Common/Clickable'
import BodyText from 'components/Luxkit/Typography/BodyText'
import FormatCurrency from 'components/Common/FormatCurrency'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import LinePlaneDepartureIcon from 'components/Luxkit/Icons/line/LinePlaneDepartureIcon'
import { getActivePackagesByRoom, PackageOptionViewByRoom } from 'selectors/offerPage/multipleRoomBookingSelectors'
import { checkCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { KEYBOARD_MODE_CSS_VAR } from 'constants/app'
import { mediaQueryUp } from 'components/utils/breakpoint'

const ToggleButtonsContainer = styled(Group)`
  padding: ${rem(8)};
  background-color: ${({ theme }) => theme.palette.neutral.default.eight};
  border-radius: ${props => props.theme.borderRadius.round};
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
`

const ToggleButton = styled(Clickable)`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: ${rem(44)};
  padding: ${rem(4)} ${rem(20)};
  border: 1px solid transparent;
  border-radius: ${props => props.theme.borderRadius.round};
  background-color: ${({ theme }) => theme.palette.neutral.default.eight};
  transition: background-color 0.2s, box-shadow 0.2s, color 0.2s, border-color 0.2s;
  outline-offset: 1px;

  ${mediaQueryUp.tablet} {
    flex: initial;
    min-width: ${rem(160)};
  }

  &:focus {
    border: var(${KEYBOARD_MODE_CSS_VAR}, 1px solid ${props => props.theme.palette.neutral.default.one});
  }

  &.is-selected {
    background-color: ${props => props.theme.palette.brand.primary.normal};
    box-shadow: ${props => props.theme.shadow.flat.small};

    &:focus {
      outline: var(${KEYBOARD_MODE_CSS_VAR}, 1px solid ${props => props.theme.palette.neutral.default.one});
    }
  }

  &:not(.is-selected):not(:disabled) {
    ${mediaHoverable} {
      &:hover {
        background-color: ${props => props.theme.palette.neutral.default.six};
      }
    }
  }
`

interface Props {
  rooms?: Array<App.Occupants>;
  offer: App.Offer;
  flights: boolean;
  cheapestSelectedOption: OfferPackageOptionView;
  isPerNightPricingTestEnabled: boolean;
  multipleRoomPackages: PackageOptionViewByRoom;
  canRedeemLuxPlusBenefits: boolean;
}

function HotelFlightsToggleButtonV2(props: Props) {
  const { offer, rooms, flights, cheapestSelectedOption, isPerNightPricingTestEnabled, multipleRoomPackages, canRedeemLuxPlusBenefits } = props
  const dispatch = useOfferPageDispatch()

  const {
    flightsAvailable,
    pricing: hotelPrice,
    memberPricing: hotelMemberPrice,
    pricingWithFlights,
    memberPricingWithFlights,
  } = useMemo(() => isPerNightPricingTestEnabled ? getPackageViewPricingPerNight(cheapestSelectedOption) : cheapestSelectedOption, [cheapestSelectedOption, isPerNightPricingTestEnabled])

  const { multipleRoomHotelPrice, multipleRoomHotelMemberPrice, multipleRoomFlightPrice, multipleRoomMemberFlightPrice } = useMemo(() => (
    Object.values(multipleRoomPackages || {}).reduce((acc, pkg) => {
      if (!pkg) return acc

      const {
        hotelPrice,
        hotelMemberPrice,
        flightPrice,
        propertyFees = 0,
      } = isPerNightPricingTestEnabled ? getPackageViewPricingPerNight(pkg) : pkg

      return {
        multipleRoomHotelPrice: acc.multipleRoomHotelPrice + hotelPrice + propertyFees,
        multipleRoomHotelMemberPrice: acc.multipleRoomHotelMemberPrice + hotelMemberPrice + propertyFees,
        multipleRoomFlightPrice: acc.multipleRoomFlightPrice + hotelPrice + propertyFees + flightPrice,
        multipleRoomMemberFlightPrice: acc.multipleRoomMemberFlightPrice + hotelMemberPrice + propertyFees + flightPrice,
      }
    }, { multipleRoomHotelPrice: 0, multipleRoomHotelMemberPrice: 0, multipleRoomFlightPrice: 0, multipleRoomMemberFlightPrice: 0 })
  ), [isPerNightPricingTestEnabled, multipleRoomPackages])

  const hotelTotalPrice = useMemo(() => {
    if (canRedeemLuxPlusBenefits && hotelMemberPrice?.price) return (rooms?.length ?? 0) > 1 ? multipleRoomHotelMemberPrice : hotelMemberPrice.price
    return (rooms?.length ?? 0) > 1 ? multipleRoomHotelPrice : hotelPrice.price
  }, [canRedeemLuxPlusBenefits, hotelMemberPrice, rooms, multipleRoomHotelPrice, multipleRoomHotelMemberPrice, hotelPrice])

  const hotelFlightTotalPrice = useMemo(() => {
    if (canRedeemLuxPlusBenefits && memberPricingWithFlights?.price) return (rooms?.length ?? 0) > 1 ? multipleRoomMemberFlightPrice : memberPricingWithFlights.price
    return (rooms?.length ?? 0) > 1 ? multipleRoomFlightPrice : pricingWithFlights?.price ?? 0
  }, [canRedeemLuxPlusBenefits, memberPricingWithFlights, rooms?.length, multipleRoomMemberFlightPrice, multipleRoomFlightPrice, pricingWithFlights])

  const onSelectHotel = useCallback(() => {
    if (flights) {
      dispatch({ type: OfferPageActions.toggleFlightsPrice, isFlightsSelected: false })
    }
  }, [flights, dispatch])

  const onSelectHotelFlights = useCallback(() => {
    if (!flights) {
      dispatch({ type: OfferPageActions.toggleFlightsPrice, isFlightsSelected: true })
    }
  }, [flights, dispatch])

  useEffect(() => {
    if (!flightsAvailable && flights) {
      dispatch({ type: OfferPageActions.toggleFlightsPrice, isFlightsSelected: false })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flightsAvailable])

  // Same flightsEnabled logic from FlashHotelOptionPriceDetails.tsx
  if (!offer.offerFlightsEnabled || config.businessTraveller.currentAccountMode === 'business') return null

  return (
    <ToggleButtonsContainer direction="horizontal" gap={4}>
      <ToggleButton className={cn({ 'is-selected': !flights })} onClick={onSelectHotel} disabled={!flightsAvailable}>
        <Group direction="vertical" horizontalAlign="center">
          <BodyText
            variant="medium"
            weight={!flights ? 'bold' : undefined}
            colour={!flights ? 'neutral-eight' : 'neutral-one'}
          >
            Hotel
          </BodyText>
          <BodyText variant="small" colour={flights ? 'neutral-three' : 'neutral-five'}>{hotelMemberPrice && 'From '}<FormatCurrency value={hotelTotalPrice} /></BodyText>
        </Group>
      </ToggleButton>
      <ToggleButton className={cn({ 'is-selected': flights })} onClick={onSelectHotelFlights} disabled={!flightsAvailable}>
        <Group direction="vertical" horizontalAlign="center">
          <BodyTextBlock
            variant="medium"
            weight={flights ? 'bold' : undefined}
            colour={flights ? 'neutral-eight' : 'neutral-one'}
            startIcon={<LinePlaneDepartureIcon />}
          >
            Hotel + flights
          </BodyTextBlock>
          {flightsAvailable && !!hotelFlightTotalPrice && <BodyText variant="small" colour={flights ? 'neutral-five' : 'neutral-three'}>{hotelMemberPrice && 'From '}<FormatCurrency value={hotelFlightTotalPrice} /></BodyText>}
          {!flightsAvailable && <BodyText variant="small" colour={flights ? 'neutral-five' : 'neutral-three'}>Unavailable for your dates</BodyText>}
        </Group>
      </ToggleButton>
    </ToggleButtonsContainer>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  rooms: state.rooms,
  offer: state.offer,
  flights: state.flights,
  cheapestSelectedOption: getCheapestSelectedPackageOptionWithDefault(appState, state),
  isPerNightPricingTestEnabled: !!getOptimizelyExperimentVariation(appState, OptimizelyExperiments.pricePerNight),
  multipleRoomPackages: getActivePackagesByRoom(state, appState),
  canRedeemLuxPlusBenefits: checkCanRedeemLuxPlusBenefits(appState),
}))(HotelFlightsToggleButtonV2)
